// Structured data for a blog post. Always emits Article + BreadcrumbList. Emits the
// page-type schema when the post declares it in its content entry:
//   content.howTo   -> HowTo      (step-by-step guides)
//   content.faq     -> FAQPage    (posts with a Q&A block)
//   content.itemList-> ItemList   (listicles / rankings)
// Keeping these driven by the content entry means the visible FAQ/steps and the schema
// never drift apart.

const ORIGIN = 'https://www.uxauditpro.com';
const PUBLISHER = {
  '@type': 'Organization',
  name: 'UXAuditPro',
  logo: { '@type': 'ImageObject', url: `${ORIGIN}/logo.png` },
};

export function buildPostSchema(post, content) {
  const url = `${ORIGIN}/blog/${post.slug}/`;
  const coverUrl = post.cover ? `${ORIGIN}${post.cover}` : undefined;
  const schemas = [];

  schemas.push({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    author: { '@type': 'Organization', name: 'UXAuditPro', url: ORIGIN },
    datePublished: post.date,
    dateModified: post.dateModified || post.date,
    publisher: PUBLISHER,
    ...(coverUrl ? { image: coverUrl } : {}),
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
    description: post.excerpt,
  });

  schemas.push({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${ORIGIN}/` },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${ORIGIN}/blog/` },
      { '@type': 'ListItem', position: 3, name: post.title, item: url },
    ],
  });

  if (content?.howTo?.steps?.length) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: content.howTo.name || post.title,
      description: content.howTo.description || post.excerpt,
      step: content.howTo.steps.map((s, i) => ({
        '@type': 'HowToStep',
        position: i + 1,
        name: s.name,
        text: s.text,
      })),
    });
  }

  if (content?.faq?.length) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: content.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  if (content?.itemList?.length) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: content.itemList.map((name, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name,
      })),
    });
  }

  return schemas;
}
